/**
 * Slicers: filter rows that narrow the points already drawn, live, with no
 * query.
 *
 * A slicer is a `FilterRow` with a `slicer` range - one list with the ordinary
 * filters, so it is saved and shared the same way (the `filters=` link param,
 * Saved Views, catalog charts, dashboard tiles). How it is applied depends on
 * the chart:
 *
 * - **Line, Scatter and Map** fetch the slicer columns alongside every point
 *   (`slice_columns` → `slice_values`) and hide the points outside the range
 *   here, so dragging a range redraws instantly from what is already loaded.
 * - **Everything else** - heatmap and histogram (binned on the server),
 *   Export, the row count, dashboard tiles - receives a narrowed slicer as an
 *   ordinary `>=` / `<=` pair (`getFilters`), which gives the same answer the
 *   slow way.
 *
 * Hidden, not dimmed: a point outside any slicer range is blanked (its Y set to
 * null), which a line draws as a gap and a scatter does not draw at all.
 */

import { allColumns, filters, type FilterRow } from '../../state/store.ts';
import { isNumericType, isTemporalType } from '../../utils/columns.ts';
import type { AnyPlotResponse, MapResponse, PlotResponse } from '../../api/types.ts';

/** A response whose points can be sliced in the browser. */
export type SliceableResponse = PlotResponse | MapResponse;

export interface SlicerRange {
  min: number | null;
  max: number | null;
}

/** Rows that are slicers on a real column. */
export function slicerRows(rows: FilterRow[] = filters.value): Array<FilterRow & { slicer: SlicerRange }> {
  return rows.filter((r): r is FilterRow & { slicer: SlicerRange } => !!r.slicer && !!r.column);
}

/** Slicer columns, once each - what a Line or Scatter request asks for. */
export function slicerColumns(rows: FilterRow[] = filters.value): string[] {
  return [...new Set(slicerRows(rows).map((r) => r.column))];
}

/** Whether a slicer excludes anything - an untouched one is a no-op. */
export function isNarrowed(range: SlicerRange | undefined): boolean {
  return !!range && (range.min != null || range.max != null);
}

/** Whether a column can be sliced: numbers and times have a range, text does not. */
export function sliceable(column: string): boolean {
  const col = allColumns.value.find((c) => c.name === column);
  if (!col) return true; // derived signals are numeric
  return isNumericType(col.type) || isTemporalType(col.type);
}

/** Whether `column` is a time column - its values are epoch seconds. */
export function isTimeColumn(column: string): boolean {
  const col = allColumns.value.find((c) => c.name === column);
  return !!col && isTemporalType(col.type);
}

/**
 * The per-point values of `column` in one XY response: the X values when it is
 * the X column, a plotted series when it is one, otherwise the slicer column the
 * server returned. Null when this response does not carry it - a slicer added
 * since the last Plot, until the next one fetches it.
 */
export function columnValues(data: SliceableResponse, column: string): ArrayLike<number | null> | null {
  if (data.plot_type === 'map') {
    if (column === data.time_column) return data.time;
    return data.slice_values?.[column] ?? null;
  }
  if (column === data.x_column) return data.x;
  const fromSlice = data.slice_values?.[column];
  if (fromSlice) return fromSlice;
  const series = data.series.find((s) => (s.column ?? s.name) === column);
  return series ? series.values : null;
}

export function isSliceable(data: AnyPlotResponse | null | undefined): data is SliceableResponse {
  return !!data && (data.plot_type === 'line' || data.plot_type === 'scatter' || data.plot_type === 'map');
}

/**
 * `data` with every point outside a narrowed slicer blanked. Returns the same
 * object when nothing is sliced, so the common path costs nothing.
 */
export function sliceResponse<T extends AnyPlotResponse>(data: T, rows: FilterRow[] = filters.value): T {
  if (!isSliceable(data)) return data;
  const keep = sliceMask(data, rows);
  if (!keep) return data;
  // A map keeps its arrays (the view is fitted to them) and is told which
  // points to leave out, so a hidden stretch also breaks the track line.
  if (data.plot_type === 'map') return { ...data, hidden: keep.map((k) => (k ? 0 : 1)) } as T;
  return {
    ...data,
    series: data.series.map((s) => ({
      ...s,
      values: s.values.map((v, i) => (keep[i] ? v : null)),
    })),
  } as T;
}

/**
 * Which points of `data` survive every narrowed slicer (1 = shown), or null
 * when no slicer applies to this response. Shared by `sliceResponse` and the
 * in-place redraw Play uses, so both hide exactly the same points.
 */
export function sliceMask(data: SliceableResponse, rows: FilterRow[] = filters.value): Uint8Array | null {
  const active = slicerRows(rows).filter((r) => isNarrowed(r.slicer));
  if (!active.length) return null;
  const n = data.plot_type === 'map' ? data.lats.length : data.x.length;
  const keep = new Uint8Array(n).fill(1);
  let applied = false;
  for (const r of active) {
    const values = columnValues(data, r.column);
    if (!values) continue;
    applied = true;
    const lo = r.slicer.min ?? -Infinity;
    const hi = r.slicer.max ?? Infinity;
    for (let i = 0; i < n; i++) {
      const v = values[i];
      if (v == null || !(v >= lo && v <= hi)) keep[i] = 0;
    }
  }
  return applied ? keep : null;
}

/** Whether any slicer currently narrows the chart. */
export function hasActiveSlices(rows: FilterRow[] = filters.value): boolean {
  return slicerRows(rows).some((r) => isNarrowed(r.slicer));
}

/* ── Mutations ──────────────────────────────────────────── */

/**
 * A slicer end as stored: whole seconds for a time, six significant digits for
 * a number. A dragged handle lands on an arbitrary float, and fourteen decimal
 * places of it in every shared link say nothing a reader can use.
 */
function roundEnd(column: string, v: number | null): number | null {
  if (v == null || !Number.isFinite(v)) return null;
  return isTimeColumn(column) ? Math.round(v) : Number(v.toPrecision(6));
}

/** Set a slicer's range. Ends at the data's own bounds are passed as open (null). */
export function setSlicerRange(id: string, range: SlicerRange): void {
  filters.set(
    filters.value.map((r) =>
      r.id === id && r.slicer
        ? { ...r, slicer: { min: roundEnd(r.column, range.min), max: roundEnd(r.column, range.max) } }
        : r,
    ),
  );
}

/** Turn a filter row into a slicer or back. A slicer starts open - it shows everything. */
export function toggleSlicer(id: string): void {
  filters.set(
    filters.value.map((r) => {
      if (r.id !== id) return r;
      if (r.slicer) {
        const { slicer: _slicer, ...plain } = r;
        return plain;
      }
      return { ...r, slicer: { min: null, max: null } };
    }),
  );
}

/**
 * Pin a slicer: replace it with real `>=` / `<=` filter rows over the same
 * range, so the next Plot queries exactly that slice at full detail rather than
 * showing the points that happened to be in the sample.
 */
export function pinSlicer(id: string): void {
  const row = filters.value.find((r) => r.id === id);
  if (!row?.slicer) return;
  const { min, max } = row.slicer;
  const value = (v: number): string =>
    isTimeColumn(row.column) ? new Date(v * 1000).toISOString().slice(0, 19).replace('T', ' ') : String(v);
  const replacement: FilterRow[] = [];
  if (min != null) replacement.push({ id: `${id}-min`, column: row.column, op: '>=', value: value(min) });
  if (max != null) replacement.push({ id: `${id}-max`, column: row.column, op: '<=', value: value(max) });
  filters.set(filters.value.flatMap((r) => (r.id === id ? replacement : [r])));
}
